import React, { useState, useEffect, useRef } from 'react';
import './DataGrid.css';

export interface GridColumn {
  field: string;
  headerName: string;
  width?: number;
  editable?: boolean;
  type?: 'string' | 'number' | 'date' | 'currency';
  valueFormatter?: (value: any) => string;
  renderCell?: (row: GridRow) => React.ReactNode;
}

export interface GridRow {
  id: string | number;
  [key: string]: any;
}

interface DataGridProps {
  columns: GridColumn[];
  rows: GridRow[];
  onRowUpdate?: (updatedRow: GridRow) => Promise<void>;

  loading?: boolean;
  height?: string | number;
  className?: string;
  showHeaders?: boolean;
  expandable?: boolean;
  renderExpandedRow?: (row: GridRow) => React.ReactNode;
  expandedRowIds?: Set<string | number>;
  onRowExpand?: (rowId: string | number) => void;
  onRowCollapse?: (rowId: string | number) => void;
}

const DataGrid: React.FC<DataGridProps> = ({
  columns,
  rows,
  onRowUpdate,

  loading = false,
  height = 500,
  className = '',
  showHeaders = true,
  expandable = false,
  renderExpandedRow,
  expandedRowIds = new Set(),
  onRowExpand,
  onRowCollapse
}) => {
  const [gridData, setGridData] = useState<GridRow[]>([]);
  const [editCell, setEditCell] = useState<{rowId: string | number, field: string, value: any} | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [hoveredRow, setHoveredRow] = useState<string | number | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
  const [isResizing, setIsResizing] = useState<{field: string, startX: number, startWidth: number} | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{top: number, left: number}>({ top: 0, left: 0 });

  // Initialize column widths from column definitions
  useEffect(() => {
    const initialWidths: {[key: string]: number} = {};
    columns.forEach(column => {
      if (column.width) {
        initialWidths[column.field] = column.width;
      }
    });
    setColumnWidths(initialWidths);
  }, [columns]);

  // Save and restore scroll position when rows change
  useEffect(() => {
    if (gridRef.current) {
      const grid = gridRef.current;
      
      // Save current scroll position before updating data
      const { scrollTop, scrollLeft } = grid;
      scrollPositionRef.current = { top: scrollTop, left: scrollLeft };
      
      // Update the data
      setGridData(rows);
      
      // Restore scroll position after the component has rendered with new data
      // Use requestAnimationFrame to ensure DOM updates have completed
      requestAnimationFrame(() => {
        // Only restore if we had a previous position
        if (scrollPositionRef.current.top > 0 || scrollPositionRef.current.left > 0) {
          grid.scrollTop = scrollPositionRef.current.top;
          grid.scrollLeft = scrollPositionRef.current.left;
        }
      });
    } else {
      setGridData(rows);
    }
  }, [rows]);

  // Add scroll event listener to handle sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (gridRef.current) {
        const { scrollTop, scrollLeft } = gridRef.current;
        setIsHeaderSticky(scrollTop > 0);
        
        // Save current scroll position on scroll
        scrollPositionRef.current = { top: scrollTop, left: scrollLeft };
      }
    };

    const gridElement = gridRef.current;
    if (gridElement) {
      gridElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridElement) {
        gridElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Handle column resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizing) {
        const deltaX = e.clientX - isResizing.startX;
        const newWidth = Math.max(50, isResizing.startWidth + deltaX); // Minimum width of 50px
        
        setColumnWidths(prev => ({
          ...prev,
          [isResizing.field]: newWidth
        }));
      }
    };

    const handleMouseUp = () => {
      setIsResizing(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleCellClick = (row: GridRow, field: string) => {
    const column = columns.find(col => col.field === field);
    if (column?.editable) {
      setEditCell({ rowId: row.id, field, value: row[field] });
      setEditValue(String(row[field] ?? ''));
    }
  };

  const handleCellBlur = async () => {
    if (editCell && isDirty) {
      // Save current scroll position before any updates
      const currentScrollTop = gridRef.current?.scrollTop || 0;
      const currentScrollLeft = gridRef.current?.scrollLeft || 0;
      
      const updatedRow = gridData.find(row => row.id === editCell.rowId);
      if (updatedRow) {
        let newValue = editValue;
        
        // Convert value based on column type
        const column = columns.find(col => col.field === editCell.field);
        if (column?.type === 'number') {
          newValue = parseFloat(editValue) as any;
        } else if (column?.type === 'currency') {
          newValue = parseFloat(editValue.replace(/[^0-9.-]+/g, '')) as any;
        }
        
        // Create updated row data
        const updatedRowData = { 
          ...updatedRow, 
          [editCell.field]: newValue
        };
        
        // Update local state immediately (optimistic UI update)
        setGridData(prev => 
          prev.map(row => 
            row.id === editCell.rowId ? updatedRowData : row
          )
        );
        
        // Call external handler if provided
        if (onRowUpdate) {
          try {
            // Call in background without awaiting to avoid UI freeze
            const updatePromise = onRowUpdate(updatedRowData);
            
            // Restore scroll position immediately after local state update
            requestAnimationFrame(() => {
              if (gridRef.current) {
                gridRef.current.scrollTop = currentScrollTop;
                gridRef.current.scrollLeft = currentScrollLeft;
              }
            });
            
            // Wait for server update to complete
            await updatePromise;
            
            // Ensure scroll position is maintained after server response
            setTimeout(() => {
              if (gridRef.current) {
                gridRef.current.scrollTop = currentScrollTop;
                gridRef.current.scrollLeft = currentScrollLeft;
              }
            }, 50);
          } catch (error) {
            console.error('Failed to update row:', error);
            // Revert to original data if update failed
            setGridData(prev => [...rows]);
            
            // Still maintain scroll position even after reverting
            setTimeout(() => {
              if (gridRef.current) {
                gridRef.current.scrollTop = currentScrollTop;
                gridRef.current.scrollLeft = currentScrollLeft;
              }
            }, 10);
          }
        }
      }
      setIsDirty(false);
    }
    setEditCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellBlur();
    } else if (e.key === 'Escape') {
      setEditCell(null);
      setIsDirty(false);
    }
  };

  const formatCellValue = (value: any, column: GridColumn) => {
    if (value === null || value === undefined) return '';
    
    if (column.valueFormatter) {
      return column.valueFormatter(value);
    }
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(Number(value));
    }
    
    if (column.type === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }
    
    return String(value);
  };

  const getColumnWidth = (column: GridColumn) => {
    const customWidth = columnWidths[column.field];
    if (customWidth) {
      return `${customWidth}px`;
    }
    return column.width ? `${column.width}px` : 'auto';
  };

  const handleResizeStart = (e: React.MouseEvent, field: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const currentWidth = columnWidths[field] || columns.find(col => col.field === field)?.width || 150;
    setIsResizing({
      field,
      startX: e.clientX,
      startWidth: currentWidth
    });
  };

  const renderCell = (row: GridRow, column: GridColumn) => {
    // If the column has a custom renderCell function, use it
    if (column.renderCell) {
      return column.renderCell(row);
    }
    
    // Otherwise render the value
    const value = row[column.field];
    return formatCellValue(value, column);
  };



  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    if (onRowExpand) {
      onRowExpand(rowId);
    }
  };

  // Handle row collapse
  const handleRowCollapse = (rowId: string | number) => {
    if (onRowCollapse) {
      onRowCollapse(rowId);
    }
  };

  const isRowExpanded = (rowId: string | number) => {
    return expandedRowIds.has(rowId);
  };

  return (
    <div className={`data-grid-container ${className} ${isResizing ? 'resizing' : ''}`} style={{ height }}>
      {loading ? (
        <div className="data-grid-loading">
          <div className="spinner"></div>
          <div>Loading data...</div>
        </div>
      ) : (
        <div className="data-grid" ref={gridRef}>
          {showHeaders && (
            <div className={`data-grid-header ${isHeaderSticky ? 'sticky' : ''}`}>
              {expandable && (
                <div className="data-grid-header-cell expand-column" style={{ width: '50px' }}>
                  
                </div>
              )}

              {columns.map(column => (
                <div 
                  key={column.field}
                  className={`data-grid-header-cell resizable ${column.field === 'actions' ? 'actions-cell' : ''}`}
                  style={{ width: getColumnWidth(column) }}
                >
                  <span className="header-content">{column.headerName}</span>
                  <div 
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, column.field)}
                    title="Drag to resize column"
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="data-grid-body">
            {gridData.length === 0 ? (
              <div className="data-grid-no-rows">
                No data available
              </div>
            ) : (
              gridData.map(row => (
                <React.Fragment key={row.id}>
                  <div 
                    className={`data-grid-row ${hoveredRow === row.id ? 'hovered' : ''} ${isRowExpanded(row.id) ? 'expanded' : ''}`}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    {expandable && (
                      <div className="data-grid-cell expand-column" style={{ width: '50px' }}>
                        <button 
                          className={`expand-btn ${isRowExpanded(row.id) ? 'expanded' : ''}`}
                          onClick={() => {
                            if (isRowExpanded(row.id)) {
                              handleRowCollapse(row.id);
                            } else {
                              handleRowExpand(row.id);
                            }
                          }}
                          title={isRowExpanded(row.id) ? 'Collapse row' : 'Expand row'}
                        >
                          {isRowExpanded(row.id) ? '▼' : '▶'}
                        </button>
                      </div>
                    )}

                    {columns.map(column => (
                      <div 
                        key={`${row.id}-${column.field}`}
                        className={`data-grid-cell ${column.editable ? 'editable' : ''} ${column.field === 'actions' ? 'actions-cell' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click when clicking on cell
                          handleCellClick(row, column.field);
                        }}
                        style={{ width: getColumnWidth(column) }}
                        data-type={column.type}
                      >
                        {editCell && 
                         editCell.rowId === row.id && 
                         editCell.field === column.field ? (
                          <input 
                            type={column.type === 'number' || column.type === 'currency' ? 'number' : 'text'}
                            value={editValue}
                            onChange={(e) => {
                              setEditValue(e.target.value);
                              setIsDirty(true);
                            }}
                            onBlur={handleCellBlur}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className="cell-editor"
                            onClick={(e) => e.stopPropagation()} // Prevent row selection when editing
                          />
                        ) : (
                          <div className="cell-content">
                            {renderCell(row, column)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Expanded row content */}
                  {expandable && isRowExpanded(row.id) && renderExpandedRow && (
                    <div className="data-grid-expanded-row">
                      <div className="expanded-row-content">
                        {renderExpandedRow(row)}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataGrid; 